import { Text } from '@driftt/dds'
import React from 'react'

import {
  archivePlaybookById,
  disablePlaybookById,
  enablePlaybookById,
} from 'api/playbooks'
import { formatTime } from 'utils/moment'

import {
  PlaybookActionsCell,
  PlaybookNameCell,
  PlaybookPriorityCell,
  PlaybookReportTypeCell,
  PlaybookStatusCell,
  PlaybookUpdatedAuthorCell,
} from './components/TableRow'

const COLUMN_SIZES = {
  PRIORITY: 90,
  NAME: 300,
  WORKSPACE: 116,
  STATUS: 70,
  TYPE: 100,
  LAST_UPDATED: 100,
  UPDATED_BY: 80,
  SCHEDULED_FOR: 120,
  ACTIONS: 60,
}

const createColumn = (config) => ({
  disableSort: false,
  ...config,
})

const createPriorityColumn = () =>
  createColumn({
    header: 'Priority',
    value: 'priority',
    cell: (playbook) => PlaybookPriorityCell(playbook),
    size: COLUMN_SIZES.PRIORITY,
  })

const createNameColumn = () =>
  createColumn({
    header: 'Playbook',
    value: 'name',
    cell: (playbook) => PlaybookNameCell(playbook),
    size: COLUMN_SIZES.NAME,
  })

const createWorkspaceColumn = (workspaces) =>
  createColumn({
    header: 'Workspace',
    value: 'workspace.name',
    cell: (playbook) => (
      <Text variant="body">{workspaces?.[playbook?.workspaceId]?.name}</Text>
    ),
    size: COLUMN_SIZES.WORKSPACE,
  })

const createStatusColumn = () =>
  createColumn({
    header: 'Status',
    value: 'status',
    size: COLUMN_SIZES.STATUS,
    cell: (playbook) => PlaybookStatusCell(playbook),
    disableSort: true,
  })

const createTypeColumn = (tableState) =>
  createColumn({
    header: 'Type',
    value: 'type',
    size: COLUMN_SIZES.TYPE,
    cell: (playbook) =>
      tableState === 'FASTLANE_ACTIVE'
        ? 'Fastlane'
        : PlaybookReportTypeCell(playbook),
    disableSort: true,
  })

const createLastUpdatedColumn = () =>
  createColumn({
    header: 'Last updated',
    value: 'last_updated',
    size: COLUMN_SIZES.LAST_UPDATED,
    cell: (playbook) => formatTime(playbook.updatedAt, 'MM/DD/YY'),
  })

const createUpdatedByColumn = (orgUsers) =>
  createColumn({
    header: 'Updated by',
    value: 'updated_by',
    size: COLUMN_SIZES.UPDATED_BY,
    cell: (playbook) => PlaybookUpdatedAuthorCell(playbook, orgUsers),
    disableSort: true,
  })

const createScheduledForColumn = () =>
  createColumn({
    header: 'Scheduled for',
    value: 'scheduled_for',
    size: COLUMN_SIZES.SCHEDULED_FOR,
    cell: (playbook) =>
      formatTime(playbook?.targeting?.dueAt, 'MMMM D, YYYY h:m A'),
  })

const createActionsColumn = (params) => ({
  id: 'actions',
  size: COLUMN_SIZES.ACTIONS,
  render: (playbook) =>
    PlaybookActionsCell({
      callback: params.onChange,
      canEnableOrDisablePlaybook:
        params.tableState === 'CHAT_ACTIVE' ||
        params.tableState === 'MEETING_BOOKING_ACTIVE',
      cloneAsBlockExclusions: params.cloneAsBlockExclusions,
      enabledPlaybookReportTypes: params.enabledPlaybookReportTypes,
      playbook,
      setPlaybookRowModalContext: params.setPlaybookRowModalContext,
      setCloneAsBlockExclusions: params.setCloneAsBlockExclusions,
      showTransientNotification: params.showTransientNotification,
    }),
})

const createColumnSet = (params) => {
  const { tableState, workspaces, orgUsers, showWorkspaces } = params

  const columns = {
    priority: createPriorityColumn(),
    name: createNameColumn(),
    workspace: createWorkspaceColumn(workspaces),
    status: createStatusColumn(),
    type: createTypeColumn(tableState),
    lastUpdated: createLastUpdatedColumn(),
    updatedBy: createUpdatedByColumn(orgUsers),
    scheduledFor: createScheduledForColumn(),
    actions: createActionsColumn(params),
  }

  const maybeWorkspaceColumn = showWorkspaces ? [columns.workspace] : []

  return { columns, maybeWorkspaceColumn }
}

const TABLE_CONFIGURATIONS = {
  CHAT_ACTIVE: (cols, workspace) => [
    cols.priority,
    cols.name,
    ...workspace,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  CHAT_ARCHIVED: (cols, workspace) => [
    cols.name,
    ...workspace,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  EMAIL_ACTIVE: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  EMAIL_DRAFTS: (cols) => [
    cols.name,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  EMAIL_SENT: (cols) => [
    cols.name,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  EMAIL_ARCHIVED: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  EMAIL_SCHEDULED: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.scheduledFor,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  FASTLANE_ACTIVE: (cols, workspace) => [
    cols.name,
    ...workspace,
    cols.status,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  FASTLANE_ARCHIVED: (cols, workspace) => [
    cols.name,
    ...workspace,
    cols.status,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  SEQUENCE_ACTIVE: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  SEQUENCE_DRAFTS: (cols) => [
    cols.name,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  SEQUENCE_ARCHIVED: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  SIGNATURE_ACTIVE: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  SIGNATURE_ARCHIVED: (cols) => [
    cols.name,
    cols.status,
    cols.type,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  MEETING_BOOKING_ACTIVE: (cols, workspace) => [
    cols.priority,
    cols.name,
    ...workspace,
    cols.status,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
  MEETING_BOOKING_ARCHIVED: (cols, workspace) => [
    cols.name,
    ...workspace,
    cols.status,
    cols.lastUpdated,
    cols.updatedBy,
    cols.actions,
  ],
}

export const makePlaybooksTableColumns = (params) => {
  const { tableState = 'CHAT_ACTIVE' } = params

  const { columns, maybeWorkspaceColumn } = createColumnSet(params)

  const configFunction = TABLE_CONFIGURATIONS[tableState]
  if (!configFunction) {
    return []
  }

  return configFunction(columns, maybeWorkspaceColumn)
}

const DEFAULT_SORT = { property: 'last_updated', dir: 'asc' }
const PRIORITY_SORT = { property: 'priority', dir: 'asc' }
const UPDATED_DESC_SORT = { property: 'last_updated', dir: 'desc' }

export const PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS = {
  CHAT: [
    {
      label: 'Active playbooks',
      value: 'CHAT_ACTIVE',
      sortProperties: PRIORITY_SORT,
    },
    {
      label: 'Archived playbooks',
      value: 'CHAT_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  FASTLANE: [
    {
      label: 'Active playbooks',
      value: 'FASTLANE_ACTIVE',
      sortProperties: UPDATED_DESC_SORT,
    },
    {
      label: 'Archived playbooks',
      value: 'FASTLANE_ARCHIVED',
      sortProperties: UPDATED_DESC_SORT,
    },
  ],
  EMAIL: [
    {
      label: 'Automated playbooks',
      value: 'EMAIL_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Draft playbooks',
      value: 'EMAIL_DRAFTS',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Sent playbooks',
      value: 'EMAIL_SENT',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived playbooks',
      value: 'EMAIL_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Scheduled playbooks',
      value: 'EMAIL_SCHEDULED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  SEQUENCE: [
    {
      label: 'Active sequences',
      value: 'SEQUENCE_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Draft sequences',
      value: 'SEQUENCE_DRAFTS',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived sequences',
      value: 'SEQUENCE_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  SIGNATURE: [
    {
      label: 'Active signatures',
      value: 'SIGNATURE_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived signatures',
      value: 'SIGNATURE_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  MEETINGS: [
    {
      label: 'Active playbooks',
      value: 'MEETING_BOOKING_ACTIVE',
      sortProperties: PRIORITY_SORT,
    },
    {
      label: 'Archived playbooks',
      value: 'MEETING_BOOKING_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
}

export const SORT_PROPERTIES = {
  PRIORITY: 'priority',
  NAME: 'name',
  LAST_UPDATED: 'last_updated',
}

export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
}

export const PLAYBOOK_CATEGORIES = {
  CHAT: true,
  EMAIL: true,
  FASTLANE: true,
  SEQUENCE: true,
  SIGNATURE: true,
  MEETINGS: true,
}

export const WORKSPACE_SUPPORTED_PLAYBOOK_TYPES = [
  'CHAT',
  'FASTLANE',
  'MEETINGS',
]

export const TABLE_STATES = {
  CHAT_ACTIVE: 'CHAT_ACTIVE',
  CHAT_ARCHIVED: 'CHAT_ARCHIVED',
  EMAIL_ACTIVE: 'EMAIL_ACTIVE',
  EMAIL_DRAFT: 'EMAIL_DRAFT',
  EMAIL_SENT: 'EMAIL_SENT',
  EMAIL_ARCHIVED: 'EMAIL_ARCHIVED',
  EMAIL_SCHEDULED: 'EMAIL_SCHEDULED',
  FASTLANE_ACTIVE: 'FASTLANE_ACTIVE',
  FASTLANE_ARCHIVED: 'FASTLANE_ARCHIVED',
  SEQUENCE_ACTIVE: 'SEQUENCE_ACTIVE',
  SEQUENCE_ARCHIVED: 'SEQUENCE_ARCHIVED',
  SEQUENCE_DRAFTS: 'SEQUENCE_DRAFTS',
  SIGNATURE_ACTIVE: 'SIGNATURE_ACTIVE',
  SIGNATURE_ARCHIVED: 'SIGNATURE_ARCHIVED',
  MEETING_BOOKING_ACTIVE: 'MEETING_BOOKING_ACTIVE',
  MEETING_BOOKING_ARCHIVED: 'MEETING_BOOKING_ARCHIVED',
}

export const PLAYBOOK_INTERACTION_MODAL = {
  DELETE: 'DELETE',
  CLONE: 'CLONE',
  MOVE: 'MOVE',
  CLONE_AS_CLP: 'CLONE_AS_CLP',
  CLONE_AS_BOT: 'CLONE_AS_BOT',
  CLONE_AS_BLOCK: 'CLONE_AS_BLOCK',
  RESTORE_ARCHIVED_BLOCKS: 'RESTORE_ARCHIVED_BLOCKS',
}

export const PLAYBOOK_CLONE_TYPES = {
  CLP: 'CLP',
  BOT: 'BOT',
  BLOCK: 'BLOCK',
}

export const PLAYBOOK_ACTIONS = {
  ARCHIVE: archivePlaybookById,
  DISABLE: disablePlaybookById,
  ENABLE: enablePlaybookById,
  RESTORE: disablePlaybookById,
}

export const TOAST_NOTIFICATIONS = {
  ARCHIVE: (name) => `${name} archived successfully.`,
  CLONE: (name) => `${name} cloned successfully.`,
  CLONE_AS_BOT: (name) => `${name} cloned successfully.`,
  CLONE_AS_CLP: (name) => `${name} cloned successfully.`,
  DELETE: (name) => `${name} deleted successfully`,
  DISABLE: (name) => `${name} turned off`,
  ENABLE: (name) => `${name} turned on`,
  RESTORE: (name) => `${name} restored successfully`,
}
